import { CameraView } from "@/utils/cam-view";
import { StoreFboPassObj } from "@faro-lotv/app-component-toolbox";
import { DepthBufferReader, LodPointCloud } from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { RefObject, useEffect, useRef, useState } from "react";
import { Box3, Camera, Vector3Tuple } from "three";
import {
  computeBoxHandlesFramingView,
  computeVisibleDataBox,
} from "./smart-clipping";
import {
  listVisiblePoints,
  sampleVisibleGrid,
  VisibleGrid,
} from "./visible-points";

/** A region box around the visible data and the view showing all its handles */
export type SmartClipping = {
  box: Box3;
  view: CameraView;
  /** The view the region was computed from, and what it showed */
  entry: { camera: Camera; visible: VisibleGrid };
};

/** Time for the requested nodes of the point cloud to show up after the camera stops */
const MIN_RENDER_DELAY_MS = 500;
/** Huge point clouds may never finish loading, sample what is there after this time */
const MAX_LOADING_WAIT_MS = 5000;
const POLL_INTERVAL_MS = 100;

enum Pass {
  /** Move to the start view */
  start = "start",
  /** Compute a first box from what the start view shows, then frame it */
  overview = "overview",
  /** Refine the box from the framed view, where the data is larger on screen */
  refine = "refine",
}

type UseSmartClippingProps = {
  /** The FBO holding the depth buffer of the point cloud, rendered without clipping */
  storeFboRef: RefObject<StoreFboPassObj | null>;
  /** The point cloud, to wait for its points to load */
  pointCloud: LodPointCloud;
  /** World positions of the static scanners of the visible data, to find floors hidden from above */
  scannerPositions: Vector3Tuple[];
  /** An overview of the data, its orientation is kept in the final view */
  startView: CameraView;
  /** Used if no data is visible */
  fallbackBox: Box3;
  /** True to compute the region */
  isActive: boolean;
  /** True while the camera is animating */
  isCameraAnimating: boolean;
  /** Animates the camera to a view */
  setCameraView(view: CameraView): void;
  /** Called with the computed region, the camera is already moving to its view */
  onComputed(region: SmartClipping): void;
};

/**
 * Computes a region box around the data the user sees and frames it, while isActive is true.
 * The data is read from the depth buffer of two views, so stray points are ignored and the point cloud is never read in full.
 */
export function useSmartClipping({
  storeFboRef,
  pointCloud,
  scannerPositions,
  startView,
  fallbackBox,
  isActive,
  isCameraAnimating,
  setCameraView,
  onComputed,
}: UseSmartClippingProps): void {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);
  const aspect = useThree((state) => state.size.width / state.size.height);
  const [reader] = useState(() => new DepthBufferReader());
  useEffect(() => () => reader.dispose(), [reader]);

  const [pass, setPass] = useState(Pass.start);
  const firstPass = useRef<Pick<SmartClipping, "box" | "entry">>(undefined);

  useEffect(() => {
    if (!isActive) {
      setPass(Pass.start);
    } else if (pass === Pass.start) {
      setPass(Pass.overview);
      setCameraView(startView);
    }
  }, [isActive, pass, setCameraView, startView]);

  useEffect(() => {
    if (!isActive || pass === Pass.start || isCameraAnimating) return;
    const start = performance.now();
    const interval = setInterval(() => {
      const elapsed = performance.now() - start;
      const isLoading =
        pointCloud.lodTreeFetcher.nodesPending > 0 &&
        elapsed < MAX_LOADING_WAIT_MS;
      const fbo = storeFboRef.current?.offscreenFbo;
      if (elapsed < MIN_RENDER_DELAY_MS || isLoading || !fbo?.depthTexture) {
        return;
      }
      clearInterval(interval);

      reader.read(fbo.depthTexture, fbo.width, fbo.height, gl);
      const visible = sampleVisibleGrid(reader, fbo.width, fbo.height, camera);
      const visibleBox = computeVisibleDataBox(
        listVisiblePoints(visible),
        scannerPositions,
      );

      if (pass === Pass.overview || !firstPass.current) {
        const box = visibleBox ?? fallbackBox.clone();
        firstPass.current = { box, entry: { camera: camera.clone(), visible } };
        setPass(Pass.refine);
        setCameraView(
          computeBoxHandlesFramingView(box, camera.quaternion, aspect),
        );
      } else {
        const box = visibleBox ?? firstPass.current.box;
        const view = computeBoxHandlesFramingView(
          box,
          camera.quaternion,
          aspect,
        );
        setCameraView(view);
        onComputed({ box, view, entry: firstPass.current.entry });
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [
    aspect,
    camera,
    fallbackBox,
    gl,
    isActive,
    isCameraAnimating,
    onComputed,
    pass,
    pointCloud,
    reader,
    scannerPositions,
    setCameraView,
    storeFboRef,
  ]);
}
